/* eslint-disable react/no-array-index-key */
import { FC, useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { StageEntryDto } from 'src/generated/services/TFinancialApi';
import { StageEntryItem } from './StageEntryItem';

interface RoundCardProps {
  roundNumber: number;
  entries: StageEntryDto[];
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return null;
  try {
    return format(new Date(dateStr), 'dd-MMM-yyyy');
  } catch {
    return dateStr;
  }
};

type RoundStatus = 'complete' | 'rejected' | 'waiting';

const getRoundStatus = (entries: StageEntryDto[]): RoundStatus => {
  const hasWaiting = entries.some((e) => {
    const s = (e.actionCategory || e.status || e.action || '').toUpperCase();
    return !s.includes('VALIDATED') && !s.includes('APPROVED') && !s.includes('ACCEPT') && !s.includes('REJECT');
  });
  if (hasWaiting) return 'waiting';
  const hasRejected = entries.some((e) => {
    const s = (e.actionCategory || e.status || e.action || '').toUpperCase();
    return s.includes('REJECT') || s.includes('FLAGGED');
  });
  if (hasRejected) return 'rejected';
  return 'complete';
};

export const RoundCard: FC<RoundCardProps> = ({ roundNumber, entries }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const roundStatus = getRoundStatus(entries);
  const roundDate = entries[entries.length - 1]?.completedAt || entries[0]?.completedAt;

  return (
    <div className='mb-12 border border-neutral-20 rounded-8 overflow-hidden bg-white'>
      <button
        type='button'
        onClick={() => setIsExpanded((v) => !v)}
        className='w-full px-16 py-12 flex items-center justify-between bg-neutral-10 hover:bg-neutral-20 transition-colors'
      >
        <div className='flex items-center gap-8'>
          <span
            className={clsx('w-8 h-8 rounded-full flex-shrink-0', {
              'bg-primary': roundStatus === 'complete',
              'bg-red-40': roundStatus === 'rejected',
              'bg-blue-30': roundStatus === 'waiting',
            })}
          />
          <span className='text-14 font-semibold text-neutral-80'>Round {roundNumber}</span>
        </div>
        <div className='flex items-center gap-12'>
          {roundDate && <span className='text-12 text-neutral-50'>{formatDate(roundDate)}</span>}
          <svg
            className={clsx('w-16 h-16 text-neutral-50 transition-transform', isExpanded && 'rotate-180')}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
          </svg>
        </div>
      </button>

      {isExpanded && (
        <div>
          {entries.map((entry, idx) => (
            <StageEntryItem key={`entry-${roundNumber}-${idx}`} entry={entry} isLast={idx === entries.length - 1} />
          ))}
        </div>
      )}
    </div>
  );
};
